import React from 'react';
import * as PropTypes from 'prop-types';
import { Button, Modal } from 'react-bootstrap';
import { consultantTypeLabel, generalCurrencyFormatter, niceDateTimeFormatter } from '../../lib/helper/formatting';

/**
 * Confirmation for deleting a CCSD cost change.
 *
 * The delete is permanent and moves the effective price, so the copy names what the code reverts
 * to and is explicit that invoices already raised are not recalculated.
 */
function DeleteCostChangeModal({ datedCost, show, isDeleting, onConfirm, onCancel }) {
  if (!datedCost) {
    return null;
  }

  const ccsdCode = datedCost.ccsdCodeCost?.ccsdCode;
  const consultantHealthInsurer = datedCost.ccsdCodeCost?.consultantHealthInsurer;
  const consultant = consultantHealthInsurer?.consultant;
  const healthInsurer = consultantHealthInsurer?.healthInsurer;
  const healthInsurerLabel =
    healthInsurer?.canHaveOverrideLabel && consultantHealthInsurer?.overrideLabel
      ? consultantHealthInsurer.overrideLabel
      : healthInsurer?.name;

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Delete this cost change?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          <strong>{ccsdCode?.reference}</strong>
          {consultant && (
            <>
              {' for '}
              <strong>{consultant.name}</strong>
            </>
          )}
          {healthInsurerLabel && <> / {healthInsurerLabel}</>}
          {datedCost.consultantType?.code && <> ({consultantTypeLabel(datedCost.consultantType.code)})</>}
        </p>
        <p>
          {datedCost.previousCost != null ? (
            <>
              The cost will revert from <strong>{generalCurrencyFormatter(datedCost.cost)}</strong> to{' '}
              <strong>{generalCurrencyFormatter(datedCost.previousCost)}</strong>.
            </>
          ) : (
            <>
              The cost of <strong>{generalCurrencyFormatter(datedCost.cost)}</strong> set from{' '}
              {niceDateTimeFormatter(datedCost.validFrom, 'DD-MM-YYYY')} will be removed. This is the only cost for this
              code, so it will be left with <strong>no price</strong> until a new one is entered.
            </>
          )}
        </p>
        <p className="mb-0 text-danger">
          <strong>This cannot be undone.</strong> Any invoices already raised against the current price will not be
          recalculated &mdash; you will need to check those separately.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm} disabled={isDeleting}>
          {isDeleting ? 'Deleting…' : 'Delete cost change'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

DeleteCostChangeModal.propTypes = {
  datedCost: PropTypes.object,
  show: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default DeleteCostChangeModal;
